import React, { useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { 
  UserCircleIcon, 
  EnvelopeIcon, 
  PhoneIcon,
  CalendarIcon,
  BriefcaseIcon,
  MapPinIcon,
  PencilIcon
} from '@heroicons/react/24/outline';
import Modal from '../components/Modal';
import toast from 'react-hot-toast';
import { format } from 'date-fns';

const Profile = () => {
  const { user } = useAuth();
  const [isEditing, setIsEditing] = useState(false);
  const [formData, setFormData] = useState({
    first_name: user?.first_name || '',
    last_name: user?.last_name || '',
    phone_number: user?.phone_number || '',
    ...(user?.user_type === 'shop_owner' && {
      business_name: user?.profile?.business_name || '',
      business_address: user?.profile?.business_address || '',
      business_phone: user?.profile?.business_phone || '',
      town: user?.profile?.town || '',
      district: user?.profile?.district || ''
    })
  });

  const handleUpdate = async (e) => {
    e.preventDefault();
    try {
      // API call to update profile
      toast.success('Profile updated successfully');
      setIsEditing(false);
    } catch (error) {
      toast.error('Failed to update profile');
    }
  };

  const getInitials = (name) => {
    if (!name) return 'U';
    return name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2);
  };

  const getRoleBadgeColor = (role) => {
    const colors = {
      owner: 'bg-purple-100 text-purple-800',
      manager: 'bg-blue-100 text-blue-800',
      sales_agent: 'bg-green-100 text-green-800',
      shop_owner: 'bg-orange-100 text-orange-800'
    };
    return colors[role] || 'bg-gray-100 text-gray-800';
  };

  const InfoItem = ({ icon: Icon, label, value }) => (
    <div className="flex items-start space-x-3">
      <div className="flex-shrink-0">
        <Icon className="h-5 w-5 text-gray-400" />
      </div>
      <div>
        <p className="text-sm font-medium text-gray-500">{label}</p>
        <p className="mt-1 text-sm text-gray-900">{value || 'Not provided'}</p>
      </div>
    </div>
  );

  const StatCard = ({ label, value }) => (
    <div className="bg-gray-50 rounded-lg p-4 text-center">
      <p className="text-sm font-medium text-gray-500">{label}</p>
      <p className="mt-1 text-xl font-semibold text-gray-900">{value}</p>
    </div>
  );

  return (
    <div>
      <div className="sm:flex sm:items-center sm:justify-between">
        <div className="sm:flex-auto">
          <h1 className="text-2xl font-semibold text-gray-900">My Profile</h1>
          <p className="mt-2 text-sm text-gray-700">
            View and manage your personal information.
          </p>
        </div>
        <div className="mt-4 sm:mt-0 sm:ml-16 sm:flex-none">
          <button
            onClick={() => setIsEditing(true)}
            className="btn-primary"
          >
            <PencilIcon className="h-4 w-4 mr-2" />
            Edit Profile
          </button>
        </div>
      </div>

      {/* Profile Header */}
      <div className="mt-8 bg-white shadow rounded-lg overflow-hidden">
        <div className="bg-gradient-to-r from-primary-500 to-primary-600 h-32"></div>
        <div className="px-6 pb-6">
          <div className="flex flex-col sm:flex-row sm:items-end -mt-12">
            <div className="flex-shrink-0">
              <div className="h-24 w-24 rounded-full bg-white p-1 shadow-lg">
                <div className="h-full w-full rounded-full bg-primary-100 flex items-center justify-center">
                  <span className="text-2xl font-bold text-primary-600">
                    {getInitials(`${user?.first_name} ${user?.last_name}`)}
                  </span>
                </div>
              </div>
            </div>
            <div className="mt-4 sm:mt-0 sm:ml-6 flex-1">
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
                <div>
                  <h2 className="text-xl font-bold text-gray-900">
                    {user?.first_name} {user?.last_name}
                  </h2>
                  <p className="text-sm text-gray-500">{user?.email}</p>
                </div>
                <div className="mt-2 sm:mt-0">
                  <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium capitalize ${getRoleBadgeColor(user?.user_type)}`}>
                    {user?.user_type?.replace('_', ' ')}
                  </span>
                </div>
              </div>
            </div>
          </div>

          {/* Stats Grid */}
          {user?.stats && (
            <div className="mt-6 grid grid-cols-1 gap-5 sm:grid-cols-3">
              {Object.entries(user.stats).map(([key, value]) => (
                <StatCard 
                  key={key} 
                  label={key.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())} 
                  value={typeof value === 'number' && key.includes('balance') ? `ZMW ${value.toLocaleString()}` : value} 
                />
              ))}
            </div>
          )}

          {/* Information Grid */}
          <div className="mt-8 grid grid-cols-1 gap-6 sm:grid-cols-2">
            <div className="space-y-4">
              <h3 className="text-lg font-medium text-gray-900">Personal Information</h3>
              <InfoItem icon={UserCircleIcon} label="Full Name" value={`${user?.first_name} ${user?.last_name}`} />
              <InfoItem icon={EnvelopeIcon} label="Email Address" value={user?.email} />
              <InfoItem icon={PhoneIcon} label="Phone Number" value={user?.phone_number} />
              <InfoItem icon={CalendarIcon} label="Member Since" value={user?.created_at ? format(new Date(user.created_at), 'dd MMM yyyy') : '-'} />
            </div>

            <div className="space-y-4">
              <h3 className="text-lg font-medium text-gray-900">Account Information</h3>
              <InfoItem icon={BriefcaseIcon} label="User Type" value={user?.user_type?.replace('_', ' ')} />
              <InfoItem icon={CalendarIcon} label="Last Login" value={user?.last_login ? format(new Date(user.last_login), 'dd MMM yyyy HH:mm') : '-'} />
              
              {user?.user_type === 'sales_agent' && user?.profile && (
                <>
                  <InfoItem icon={BriefcaseIcon} label="Employee ID" value={user.profile.employee_id || '-'} />
                  <InfoItem icon={BriefcaseIcon} label="Commission Rate" value={user.profile.commission_rate ? `${user.profile.commission_rate}%` : '-'} />
                </>
              )}

              {user?.user_type === 'shop_owner' && user?.profile && (
                <>
                  <InfoItem icon={BriefcaseIcon} label="Business Name" value={user.profile.business_name} />
                  <InfoItem icon={MapPinIcon} label="Location" value={`${user.profile.town}, ${user.profile.district || ''}`} />
                  <InfoItem icon={PhoneIcon} label="Business Phone" value={user.profile.business_phone || '-'} />
                  <InfoItem icon={BriefcaseIcon} label="Credit Limit" value={`ZMW ${user.profile.credit_limit?.toLocaleString() || 0}`} />
                </>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Edit Profile Modal */}
      <Modal
        isOpen={isEditing}
        onClose={() => setIsEditing(false)}
        title="Edit Profile"
        size="lg"
      >
        <form onSubmit={handleUpdate}>
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="form-label">First Name</label>
                <input
                  type="text"
                  required
                  className="form-input"
                  value={formData.first_name}
                  onChange={(e) => setFormData({...formData, first_name: e.target.value})}
                />
              </div>
              <div>
                <label className="form-label">Last Name</label>
                <input
                  type="text"
                  required
                  className="form-input"
                  value={formData.last_name}
                  onChange={(e) => setFormData({...formData, last_name: e.target.value})}
                />
              </div>
            </div>

            <div>
              <label className="form-label">Phone Number</label>
              <input
                type="tel"
                className="form-input"
                value={formData.phone_number}
                onChange={(e) => setFormData({...formData, phone_number: e.target.value})}
              />
            </div>

            {user?.user_type === 'shop_owner' && (
              <>
                <div>
                  <label className="form-label">Business Name</label>
                  <input
                    type="text"
                    className="form-input"
                    value={formData.business_name}
                    onChange={(e) => setFormData({...formData, business_name: e.target.value})}
                  />
                </div>

                <div>
                  <label className="form-label">Business Address</label>
                  <textarea
                    className="form-input"
                    rows="2"
                    value={formData.business_address}
                    onChange={(e) => setFormData({...formData, business_address: e.target.value})}
                  />
                </div>

                <div>
                  <label className="form-label">Business Phone</label>
                  <input
                    type="tel"
                    className="form-input"
                    value={formData.business_phone}
                    onChange={(e) => setFormData({...formData, business_phone: e.target.value})}
                  />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="form-label">Town</label>
                    <input
                      type="text"
                      className="form-input"
                      value={formData.town}
                      onChange={(e) => setFormData({...formData, town: e.target.value})}
                    />
                  </div>
                  <div>
                    <label className="form-label">District</label>
                    <input
                      type="text"
                      className="form-input"
                      value={formData.district}
                      onChange={(e) => setFormData({...formData, district: e.target.value})}
                    />
                  </div>
                </div>
              </>
            )}

            <div className="flex justify-end space-x-3 pt-4">
              <button
                type="button"
                onClick={() => setIsEditing(false)}
                className="btn-secondary"
              >
                Cancel
              </button>
              <button
                type="submit"
                className="btn-primary"
              >
                Save Changes
              </button>
            </div>
          </div>
        </form>
      </Modal>
    </div>
  );
};

export default Profile;